import * as React from 'react';
import { Button, Typography, Icon } from '@/components/common';
import { InputDate } from '@/components/form/input-date/InputDate';
import Icons from '@/const/icons';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import {
	RadioButtonOption,
	RadioButtonV2,
} from '@/components/common/radioButtonV2/RadioButtonV2';

interface DatePickerFilterProps {
	onApply?: (data: { sort?: string; from?: string; to?: string }) => void;
	onCancel?: () => void;
	onClearAll?: () => void;
	initialFrom?: string;
	initialTo?: string;
	initialSort?: string;
}

export const DatePickerFilter: React.FC<DatePickerFilterProps> = ({
	onApply,
	onCancel,
	onClearAll,
	initialFrom,
	initialTo,
	initialSort,
}) => {
	const { register } = useForm();
	const [sortOption, setSortOption] = React.useState<string | null>(
		initialSort || null
	);
	const [fromDate, setFromDate] = React.useState<string>(initialFrom || '');
	const [toDate, setToDate] = React.useState<string>(initialTo || '');

	const radioOptions: RadioButtonOption[] = [
		{ id: 'oldest', value: 'STATUS_DATE_ASC', label: 'Oldest to newest' },
		{ id: 'newest', value: 'STATUS_DATE_DESC', label: 'Newest to oldest' },
	];

	const handleApply = () => {
		onApply?.({
			sort: sortOption || undefined,
			from: fromDate || undefined,
			to: toDate || undefined,
		});
	};

	return (
		<div className="bg-white rounded-lg shadow-shadow2 w-[557px] overflow-hidden border border-opacity-primary">
			<div className="flex divide-x divide-grey-100 min-h-[220px]">
				{/* Left side: Options */}
				<div className="w-[247px] py-8">
					<div className="px-6 mb-2">
						<RadioButtonV2
							options={radioOptions}
							value={sortOption || ''}
							onChange={(val) => {
								setSortOption(val);
							}}
							orientation="vertical"
							classNameContainer="!mb-4"
							labelPlacement="left"
						/>
					</div>
					<button
						type="button"
						className={clsx(
							'w-full flex items-center justify-between px-6 py-3 text-left bg-grey-5'
							// {
							// 	'bg-grey-5': isCustomActive,
							// 	'hover:bg-grey-5': !isCustomActive,
							// }
						)}
					>
						<Typography type="regular-16" className="text-black">
							Custom date range
						</Typography>

						<Icon
							src={Icons.arrowRight3}
							className="!size-6 !text-black"
							width={24}
						/>
					</button>
				</div>

				{/* Right side: Custom range inputs */}
				<div className="flex-1 p-6 space-y-4">
					<div className="space-y-1.5">
						<InputDate
							name="after"
							title="After"
							register={register}
							setValueInput={(_name: string, value: string) =>
								setFromDate(value)
							}
							dateDefault={fromDate}
							placeholder="Select date"
						/>
					</div>
					<div className="space-y-1.5">
						<InputDate
							name="before"
							title="Before"
							register={register}
							setValueInput={(_name: string, value: string) => setToDate(value)}
							dateDefault={toDate}
							placeholder="Select date"
						/>
					</div>
				</div>
			</div>

			{/* Footer */}
			<div className="p-4 border-t border-grey-100 flex items-center justify-between">
				<button
					type="button"
					onClick={() => {
						setSortOption(null);
						setFromDate('');
						setToDate('');
						onClearAll?.();
					}}
					className="flex items-center gap-x-2 px-2 py-1 hover:opacity-70 transition-opacity"
				>
					<Icon src={Icons.broom} className="!size-5 text-primary" />
					<Typography type="regular-14">Clear All</Typography>
				</button>

				<div className="flex items-center gap-x-3">
					<Button
						decoration="line-primary"
						size="small"
						className="px-6 h-9 rounded-full"
						onClick={onCancel}
						label="Cancel"
					/>
					<Button
						decoration="primary"
						size="small"
						className="px-8 h-9 rounded-full"
						onClick={handleApply}
						label="Apply"
						disabled={!sortOption && (!fromDate || !toDate)}
					/>
				</div>
			</div>
		</div>
	);
};
